import type { EditPolicy } from "@wms/domain";

/**
 * CW31-B08: the fields a plain save may change in a saved record of this status (`edit_policy` of the meta — the
 * same lists the server guard applies). null — the entity has no per-field policy: every shown field is editable.
 */
export function allowedFields(policy: EditPolicy | null | undefined, status: unknown): Set<string> | null {
  if (!policy) return null;
  const key = String(status ?? "");
  return new Set(policy.editable[key] ?? policy.editable["*"] ?? []);
}

/** How to correct the fixed requisites in this status («Исправить отдельным документом»), or null. */
export function fixFor(policy: EditPolicy | null | undefined, status: unknown): string | null {
  if (!policy?.fix) return null;
  return policy.fix[String(status ?? "")] ?? policy.fix["*"] ?? null;
}

const empty = (v: unknown) => v === undefined || v === null || v === "";

/** The value of the form equals the stored one: «2026-10-30» = «2026-10-30», 12 = «12.000», empty = null. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (empty(a) && empty(b)) return true;
  if (empty(a) || empty(b)) return false;
  if (typeof a === "object" || typeof b === "object") return JSON.stringify(a) === JSON.stringify(b);
  if (String(a) === String(b)) return true;
  const na = Number(a);
  const nb = Number(b);
  return typeof a !== "boolean" && typeof b !== "boolean" && !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
}

/**
 * CW31-B08: the body of a plain save under an edit policy — only the allowed fields, and of them only the ones that
 * differ from the stored record. A fixed requisite is never sent, even unchanged, so the server guard has nothing
 * to refuse; without a policy the body is left as it was.
 */
export function onlyChangedAllowed(body: Record<string, unknown>, saved: Record<string, unknown> | null | undefined, allowed: Set<string> | null): Record<string, unknown> {
  if (!allowed) return body;
  const result: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(body)) {
    if (!allowed.has(name)) continue;
    if (saved && sameValue(value, saved[name])) continue;
    result[name] = value;
  }
  return result;
}
